/**
 * PD-16016 — what a CUSTOMER is told when a payment fails.
 *
 * Gateway failures arrive as the processor's own text, which is written for
 * whoever integrated it rather than for the person trying to pay. The worst of
 * them name the vendor and a numeric code — "Adyen error 803: PaymentDetail not
 * found" reached a customer on the HF portal — and none of them say what to do
 * next.
 *
 * A decline reason IS useful and is passed through: "insufficient funds" or
 * "expired card" tells the customer which card to try instead. Everything else
 * collapses to one instruction. The full gateway text is never discarded — it
 * is logged and recorded on the failed transaction, where support can read it.
 */

/** Fragments that identify a message as plumbing rather than a decline. */
const INTERNAL_FAILURE_PATTERNS: ReadonlyArray<RegExp> = [
	/\berror\s+\d+\b/i,
	/paymentdetail/i,
	/\bhttp\b/i,
	/\b(?:4\d{2}|5\d{2})\b\s*(?:status|response)?/i,
	/merchant\s*account/i,
	/\bapi\b/i,
	/credential/i,
	/\bnot configured\b/i,
	/\bunauthori[sz]ed\b/i,
	/\btoken\b/i,
	/\bnull\b|\bundefined\b/i,
	// A tenant's choice of payment processor is its own business, not the
	// customer's, and the gateways put their own name in most failure text
	// ("Adyen could not store the payment method: Refused"). Any message that
	// names one is treated as internal, whatever else it says.
	/\badyen\b/i,
	/\bnmi\b/i,
	/cyber\s*source/i,
	/authori[sz]e[\s._-]*net/i,
	// A risk-engine block ("FRAUD", "Issuer Suspected Fraud") is reported as a
	// refusal reason. It tells a customer nothing they can act on, and reads as
	// an accusation.
	/\bfraud\b/i,
];

/** The instruction a customer can actually act on. */
export const GENERIC_PAYMENT_FAILURE_MESSAGE =
	'We could not complete this payment. Please try a different payment method, or re-add your card and try again.';

/**
 * The same instruction for a customer SAVING a card or bank account, where no
 * payment was attempted and "re-add your card" would be the step that failed.
 */
export const GENERIC_PAYMENT_METHOD_SAVE_FAILURE_MESSAGE =
	'We could not save this payment method. Please check the details and try again, or use a different card or account.';

/**
 * Whether gateway text is safe to show a customer as-is: present, and free of
 * processor names and internal plumbing.
 */
export function isCustomerSafePaymentText(
	gatewayText: string | null | undefined,
): boolean {
	const message = gatewayText?.trim() ?? '';

	if (message === '') {
		return false;
	}

	return !INTERNAL_FAILURE_PATTERNS.some((pattern) => pattern.test(message));
}

export function toCustomerFacingPaymentError(
	gatewayError: string | null | undefined,
	fallback: string = GENERIC_PAYMENT_FAILURE_MESSAGE,
): string {
	if (gatewayError == null || !isCustomerSafePaymentText(gatewayError)) {
		return fallback;
	}

	return gatewayError.trim();
}
